import enhancedMatchContact from './enhancedMatchContact';

/**
 * Session Contact Enhancement Utility
 *
 * This utility is part of the enhanced contact matching solution that ensures contact names
 * persist through all message operations, particularly after send failures.
 *
 * Purpose:
 * - Replaces auto-generated custom contacts with real contacts from the contacts page
 * - Maintains contact information consistency across session updates
 * - Works in conjunction with enhancedMatchContact() for comprehensive contact resolution
 *
 * Usage Context:
 * - Called before Redux state updates in sendMessage operations
 * - Applied during session list fetching to enhance all sessions
 * - Used in UI components to display proper contact names
 *
 * @param {Object} session - The session object to enhance
 * @returns {Object} - Enhanced session object with real contact if found
 */
export default function enhanceSessionContact(session) {
  if (!session || !session.contact || !session.remote) {
    return session;
  }

  // Only enhance if current contact is a custom contact
  if (session.contact.customContact === true) {
    const betterContact = enhancedMatchContact(session.remote);

    if (betterContact && !betterContact.customContact) {
      return {
        ...session,
        contact: betterContact,
        contactArr: session.contactArr ? session.contactArr.map((contact, index) => (
          (index === 0 && contact.customContact === true) ? betterContact : contact
        )) : [betterContact],
        tempContactArr: session.tempContactArr ? session.tempContactArr.map((contact, index) => (
          (index === 0 && contact.customContact === true) ? betterContact : contact
        )) : [betterContact],
      };
    }
  }

  return session;
}

/**
 * Enhances an array of sessions by replacing custom contacts with real ones when possible
 * @param {Array} sessions - Array of session objects to enhance
 * @returns {Array} - Array of enhanced session objects
 */
export function enhanceSessionsContacts(sessions) {
  if (!Array.isArray(sessions)) {
    return sessions;
  }

  return sessions.map(enhanceSessionContact);
}
